/* eslint-disable no-useless-escape */
import { v4 as uuid } from 'uuid';
import { forkJoin, mergeMap, of } from "rxjs";
import { Injectable } from '@angular/core';
import { TranslateService } from "@ngx-translate/core";
import { Store } from '@ngxs/store';
import * as FlatModels from '@medsurf/flat-models';
import * as FlatActions from '@medsurf/flat-actions';
import * as FlatStates from '@medsurf/flat-states';
import * as FlatFacades from '../../internal';

@Injectable()
export class NodeEntityFacade {
  constructor(protected store: Store) {
  }

  static getPageCopyCountAndRoute(nodeRoute: string,
                                  parentNodeId: string | null,
                                  nodeId: string | null,
                                  store: Store) {

    const typedNodes = store.selectSnapshot(FlatStates.NodeEntityState.typedEntities$);
    const typedElearnings = store.selectSnapshot(FlatStates.NodeEntityState.typedElearnings$);
    
    const parentNode = parentNodeId ? typedNodes[parentNodeId] : null;
    const siblings = (parentNode ? parentNode.children : Object.keys(typedElearnings)).filter(id => id !== nodeId);
    
    // Get all current child routes
    const siblingRoutes = siblings
      .map(id => typedNodes[id].route ?? null)
      .filter(route => route !== null);
    
    // Find free child route
    const preferredRoute = NodeEntityFacade.getRouteString(nodeRoute);
    const maxCount = siblingRoutes.reduce<number | undefined>((max, route) => {
      const match = route.match(new RegExp(`^${preferredRoute}(?:-(?<count>[0-9]+)|)$`))
      if (match) {
        const count = match?.groups?.['count'] ? Number(match.groups['count']) : 0;
        if (max === undefined || count > max) {
          return count;
        }
      }
      return max;
    }, undefined)

    if (maxCount === undefined) return {route: preferredRoute, pageCopyCount: 0 };

    const pageCopyCount = maxCount + 1;
    return { route: `${preferredRoute}-${pageCopyCount}`, pageCopyCount };
  }
  
  static getRouteString(value: string) {
    if (!value) {
      return '';
    }
    return value
    .toString()                   // Cast to string (optional)
    .toLowerCase()
    .replace('ä', 'ae')
    .replace('ö', 'oe')
    .replace('ü', 'ue')
    .replace('ß', 'ss')
    .normalize('NFKD')            // The normalize() using NFKD method returns the Unicode Normalization Form of a given string.
    .replace( /[\u0300-\u036f]/g, '' )
    .trim()                       // Remove whitespace from both sides of a string (optional)
    .replace(/\s+/g, '-')         // Replace spaces with -
    .replace(/[^\w\-]+/g, '')     // Remove all non-word chars
    .replace(/\_/g,'-')           // Replace _ with -
    // eslint-disable-next-line no-useless-escape
    .replace(/\-\-+/g, '-')       // Replace multiple - with single -
    .slice(0, 100)
    // eslint-disable-next-line no-useless-escape
    .replace(/\-$/g, '');
  }

  //<editor-fold desc="Selectors">

  //</editor-fold>

  //<editor-fold desc="Requests">
  
  static detachNode(store: Store,
                    id: string,
                    clusterParentUpdate = false) {
    // Get Data
    const typedNodes = store.selectSnapshot(FlatStates.NodeEntityState.typedEntities$);
    
    // Get Node
    const node = structuredClone(typedNodes[id]);
    
    // Get Parent Node
    if (!node.parentId) {
      throw new FlatModels.ErrorControlModels.MedsurfError(
        'notification.elearning_cannot_be_removed',
        FlatModels.ErrorControlModels.ErrorLevel.enum.error,
        FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
        "LIBS.FlatFacades.NodeEntityFacade.detachNode"
      );
    }
    const parentNode = structuredClone(typedNodes[node.parentId]);
    parentNode.children.splice(parentNode.children.findIndex(id => id === node.id), 1);
    
    // Remove Node
    return store.dispatch(new FlatActions.NodeEntityActions.RemoveEntity(node)).pipe(
      mergeMap((data) => {
        if (clusterParentUpdate) {
          return of({data, parentNode});
        } else {
          return store.dispatch(new FlatActions.NodeEntityActions.RearrangeNodes(parentNode, parentNode.children)).pipe(
            mergeMap(() =>
              store.dispatch(new FlatActions.NodeEntityActions.UpdateEntity(parentNode))
            )
          );
        }
      }),
    );
  }
  
  static detachNodes(store: Store,
                     ids: string[]) {
    if (ids.length === 0) {
      return of([]);
    }
    
    // Remove Nodes
    return of({}).pipe(
      mergeMap(() =>
        forkJoin(ids.map(id => NodeEntityFacade.detachNode(store, id, true)))
      ),
      mergeMap((data: {data: Store, parentNode: FlatModels.NodeEntityModels.NodeEntityType}[]) => {
        const nodeMap = data.reduce((arr, arrangeData) => {
          const parentNode = structuredClone(arr.get(arrangeData.parentNode.id));
          if (parentNode) {
            parentNode.children = arrangeData.parentNode.children.filter((id: string) => parentNode.children.includes(id));
            arr.set(arrangeData.parentNode.id, parentNode);
          } else {
            arr.set(arrangeData.parentNode.id, arrangeData.parentNode);
          }
          return arr;
        }, new Map<string, FlatModels.NodeEntityModels.NodeEntityType>())
        const nodes = Array.from(nodeMap.values());
        if (nodes.length > 0) {
          return forkJoin(nodes.map((node) =>
            store.dispatch(new FlatActions.NodeEntityActions.RearrangeNodes(node, node.children)).pipe(
              mergeMap(() =>
                store.dispatch(new FlatActions.NodeEntityActions.UpdateEntity(node))
              )
            )
          ));
        }
        return of(data);
      }),
    );
  }
  
  protected static copyNode(store: Store,
                            mappings: FlatModels.BulkEditAuthorModels.BulkEditMapping,
                            id: string,
                            parentNode: FlatModels.NodeEntityModels.NodeEntityType,
                            asTraining: boolean | null,
                            withAnnotations: boolean,
                            clusterParentUpdate = false) {
    // Get Data
    const typedNodes = store.selectSnapshot(FlatStates.NodeEntityState.typedEntities$);
    const typedPages = store.selectSnapshot(FlatStates.PageEntityState.typedEntities$);
    
    // Get Node
    const node = structuredClone(typedNodes[id]);
    
    // Get Page
    const page = structuredClone(typedPages[node.id]);
    
    // Update Node
    node.id = uuid();
    node.shopId = null;
    node.parent = parentNode.id;
    node.parentId = parentNode.id;
    node.position = parentNode.children.length;

    // update route with nr. count
    const { route } = NodeEntityFacade.getPageCopyCountAndRoute(node.route || page.title, parentNode?.id, null, store);
    node.route = route;
    
    // Update Node
    parentNode.children.push(node.id);
    
    // Copy Node
    return of(node.id).pipe(
      mergeMap(() =>
        FlatFacades.PageEntityFacade.copyPage(store, mappings, node.page, node, asTraining, withAnnotations, true)
      ),
      mergeMap(() =>
        store.dispatch(new FlatActions.NodeEntityActions.AddEntity(node))
      ),
      mergeMap((data) => {
        if (clusterParentUpdate) {
          return of(data);
        }
        return store.dispatch(new FlatActions.NodeEntityActions.UpdateEntity(parentNode));
      }),
      mergeMap(() => of(node.id)),
    );
  }
  
  static copyNodes(store: Store,
                   mappings: FlatModels.BulkEditAuthorModels.BulkEditMapping,
                   ids: string[],
                   parentNode: FlatModels.NodeEntityModels.NodeEntityType,
                   asTraining: boolean | null,
                   withAnnotations: boolean) {
    if (ids.length === 0) {
      return of([]);
    }
    
    // Copy Nodes
    return forkJoin(ids.map(id => NodeEntityFacade.copyNode(store, mappings, id, parentNode, asTraining, withAnnotations, true))).pipe(
      mergeMap((selectIds) =>
        store.dispatch(new FlatActions.NodeEntityActions.UpdateEntity(parentNode)).pipe(
          mergeMap(() => of(selectIds))
        )
      ),
    )
  }

  static removeNode(store: Store,
                    id: string,
                    elearnings: FlatModels.NodeEntityModels.ElearningNode[]) {
    // Get Data
    const typedNodes = store.selectSnapshot(FlatStates.NodeEntityState.typedEntities$);

    // Get Node
    const node = structuredClone(typedNodes[id]);
    
    // Get Node Children
    const childNodes = node.children.reduce((arr, child) => {
      const entity = structuredClone(typedNodes[child]);
      if (entity) {
        arr.push(entity);
      }
      return arr;
    }, [] as FlatModels.NodeEntityModels.NodeEntityType[]);
    
    // Check For Elearning Node
    if (node.parentId) {
      // Remove Node
      return store.dispatch(new FlatActions.BulkEditAuthorActions.RemoveNode(id)).pipe(
        mergeMap((data) => {
          if (childNodes.length === 0) {
            return of(data);
          }
          return forkJoin(childNodes.map(childNode => store.dispatch(new FlatActions.NodeEntityActions.RemoveEntity(childNode))));
        }),
        mergeMap(() =>
          store.dispatch(new FlatActions.NodeEntityActions.RemoveEntity(node))
        ),
      );
    } else {
      if (Array.isArray(elearnings)) {
        // Update Elearnings
        elearnings.splice(elearnings.findIndex(c => c.id === node.id), 1);
        
        // Remove Node
        return store.dispatch(new FlatActions.BulkEditAuthorActions.RemoveNode(id)).pipe(
          mergeMap((data) => {
            if (childNodes.length === 0) {
              return of(data);
            }
            return forkJoin(childNodes.map(childNode => store.dispatch(new FlatActions.NodeEntityActions.RemoveEntity(childNode))));
          }),
          mergeMap(() => {
            return store.dispatch(new FlatActions.NodeEntityActions.RearrangeNodes(null, elearnings));
          }),
          mergeMap(() =>
            store.dispatch(new FlatActions.NodeEntityActions.RemoveEntity(node))
          ),
        );
      } else {
        throw new FlatModels.ErrorControlModels.MedsurfError(
          'notification.elearnings_not_defined',
          FlatModels.ErrorControlModels.ErrorLevel.enum.error,
          FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
          "LIBS.FlatFacades.NodeEntityFacade.removeNode"
        );
      }
    }
  }

  static removeNodes(store: Store,
                     ids: string[],
                     elearnings: FlatModels.NodeEntityModels.ElearningNode[]) {
    if (ids.length === 0) {
      return of([]);
    }
    
    // Get Data
    const typedNodes = store.selectSnapshot(FlatStates.NodeEntityState.typedEntities$);
    
    // Get Parent Nodes
    const parentNodeMap: Map<string, FlatModels.NodeEntityModels.NodeEntityType> = new Map();
    for (const id of ids) {
      const node = typedNodes[id];
      if (node && node.parentId) {
        let parentNode = parentNodeMap.get(node.parentId);
        if (!parentNode) {
          parentNode = structuredClone(typedNodes[node.parentId]);
        }
        if (parentNode) {
          parentNode.children = parentNode.children.filter((c) => c !== id);
          parentNodeMap.set(parentNode.id, parentNode);
        }
      }
    }
    
    const parentNodes = Array.from(parentNodeMap.values());

    // Remove Nodes
    return of({}).pipe(
      mergeMap(() =>
        forkJoin(ids.map(id => NodeEntityFacade.removeNode(store, id, elearnings)))
      ),
      mergeMap(() => {
        return parentNodes.length > 0 ? forkJoin(parentNodes.map((node) => store.dispatch(new FlatActions.NodeEntityActions.UpdateEntity(node)))) : of({})
      }),
      mergeMap(() => {
        return parentNodes.length > 0 ? forkJoin(parentNodes.map((node) => store.dispatch(new FlatActions.NodeEntityActions.RearrangeNodes(node, node.children)))) : of({})
      })
    );
  }
  
  static removeNodesByPageId(store: Store,
                             id: string) {
    // Get Nodes
    const nodes = store.selectSnapshot(FlatStates.NodeEntityState.entities$);
    const ids = nodes.filter(n => n.page === id).map(n => n.id);
    const elearnings = structuredClone(store.selectSnapshot(FlatStates.NodeEntityState.elearnings$));
    
    if (ids.length === 0) {
      return of(undefined);
    }
    
    // Remove Nodes
    return of(ids).pipe(
      mergeMap((ids) =>
        NodeEntityFacade.removeNodes(store, ids, elearnings)
      ),
    );
  }

  //</editor-fold>
}
